import React, { useEffect, useState } from "react";
import { Heart, Loader2, RefreshCw, Users, TrendingUp, Download } from "lucide-react";
import api from "../console/api";

/* /admin/wishlist-heatmap , top-N most-hearted varieties across all customer
   wishlists. Helps Kayla see what to grow more of. Empty-friendly. */

const AUBERGINE  = "#3E2346";
const PLUM       = "#5B2A4F";
const GOLD       = "#C9A268";
const PARCH_EDGE = "#E0D1C8";

export default function AdminWishlistHeatmap() {
  const [data, setData] = useState({ total_wishlists: 0, total_hearts: 0, rows: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/wishlists/heatmap", { params: { limit: 10 } });
      setData(r.data || { total_wishlists: 0, total_hearts: 0, rows: [] });
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!data.rows.length) return;
    const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    const lines = ["Rank,Variety,Category,Hearts,Customers"];
    data.rows.forEach((r, i) => {
      lines.push([i + 1, esc(r.name), esc(r.category || ""), r.hearts, esc((r.customers || []).join("; "))].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wishlist-heatmap-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => { load(); }, []);

  const maxHearts = Math.max(1, ...data.rows.map((r) => r.hearts));

  return (
    <div data-testid="admin-wishlist-heatmap" className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>
            ✦ Customers
          </div>
          <h1 className="font-serif text-4xl mt-1.5" style={{ color: AUBERGINE }}>
            Wishlist Heatmap
          </h1>
          <p className="font-serif italic mt-1.5 text-[#6E5849] max-w-3xl text-sm leading-relaxed">
            The 10 varieties customers heart the most. Helps you decide what to grow more of.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="heatmap-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-[10px] tracking-[0.25em] uppercase font-semibold border"
          style={{ borderColor: AUBERGINE, color: AUBERGINE }}
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
        <button
          type="button"
          onClick={exportCsv}
          disabled={loading || !data.rows.length}
          data-testid="heatmap-export-csv"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-[10px] tracking-[0.25em] uppercase font-semibold"
          style={{ backgroundColor: AUBERGINE, color: "#FBF6E9", opacity: data.rows.length ? 1 : 0.45 }}
          title="Download top-10 wishlist as CSV"
        >
          <Download className="w-3 h-3" /> Export CSV
        </button>
        </div>
      </header>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Tile label="Customers with a wishlist" value={data.total_wishlists} Icon={Users} testid="heatmap-tile-wishlists" />
        <Tile label="Total hearts placed" value={data.total_hearts} Icon={Heart} testid="heatmap-tile-hearts" />
        <Tile label="Varieties in top-10" value={data.rows.length} Icon={TrendingUp} testid="heatmap-tile-rows" />
      </div>

      {/* Heatmap rows */}
      {loading ? (
        <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : data.rows.length === 0 ? (
        <div className="bg-white border border-dashed rounded p-10 text-center text-[#7A6A55]" style={{ borderColor: PARCH_EDGE }} data-testid="heatmap-empty">
          No customer wishlists yet. Once customers start hearting varieties in their HQ catalogue, this list will fill in.
        </div>
      ) : (
        <ol className="space-y-2" data-testid="heatmap-rows">
          {data.rows.map((row, i) => {
            const pct = Math.round((row.hearts / maxHearts) * 100);
            const isOpen = expanded === row.variety_id;
            return (
              <li key={row.variety_id}>
                <article
                  className="bg-white border rounded-md overflow-hidden"
                  style={{ borderColor: PARCH_EDGE }}
                  data-testid={`heatmap-row-${row.variety_id}`}
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : row.variety_id)}
                    className="w-full p-3 grid grid-cols-[40px_1fr_auto] gap-3 items-center text-left"
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center font-serif text-sm"
                      style={{ backgroundColor: AUBERGINE, color: "#FBF6E9" }}
                    >
                      {i + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-serif text-base truncate" style={{ color: AUBERGINE }}>{row.name}</div>
                      <div className="mt-1.5 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "#F3E9D2" }}>
                        <div
                          className="h-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: GOLD }}
                        />
                      </div>
                      <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mt-1">{row.category || ""}</div>
                    </div>
                    <div className="text-right">
                      <div className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: PLUM }}>
                        <Heart className="w-3 h-3" fill="#A33030" stroke="#A33030" />
                        {row.hearts}
                      </div>
                      <div className="text-[9px] opacity-60">
                        {row.customers?.length || 0} customer{row.customers?.length === 1 ? "" : "s"}
                      </div>
                    </div>
                  </button>
                  {isOpen && (row.customers || []).length > 0 && (
                    <div className="px-4 py-3 bg-[#FBF6E9] border-t text-xs" style={{ borderColor: PARCH_EDGE }} data-testid={`heatmap-emails-${row.variety_id}`}>
                      <div className="text-[10px] tracking-[0.25em] uppercase font-semibold mb-1" style={{ color: PLUM }}>
                        Hearted by
                      </div>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-0.5 text-[#3C2A1E]">
                        {row.customers.map((email) => (
                          <li key={email} className="truncate">{email}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </article>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function Tile({ label, value, Icon, testid }) {
  return (
    <div className="bg-white border rounded p-4" style={{ borderColor: PARCH_EDGE }} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: GOLD }} />
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>{label}</div>
      </div>
      <div className="font-serif text-3xl mt-1" style={{ color: AUBERGINE }}>{value}</div>
    </div>
  );
}
